import { PROTOCOL_WORDING } from '@/lib/protocol-wording';
import type {
  VersionedCounts,
  VersionedFilterChip,
  VersionedListSpec,
  VersionedSearch,
} from '@/lib/versioned-filters';
import {
  activeVersionedFilters,
  filterVersioned,
  hasActiveVersionedFilters,
  hasHiddenVersionedFilter,
  readVersionedFilters,
  resetVersionedFilters,
  versionedAuthors,
  versionedCountLabel,
  versionedCounts,
  versionedFiltersOf,
  versionedSearchOf,
} from '@/lib/versioned-filters';
import type { ProtocolCard, ProtocolFilters } from '@matter_extractor/shared';
import { PROTOCOL_FILTERS_DEFAULT } from '@matter_extractor/shared';

/**
 * What the Protocoles list adds to the common rule set of §4: NOTHING (refonte
 * modulaire §15).
 *
 * §15, word for word: « Aucun filtre ni tri propre : les filtres et tris communs
 * suffisent. » A criterion adds two dimensions and a persona two; a protocol
 * adds none, and this file is the measure of the layer rather than a formality -
 * an object that contributes an EMPTY spec still gets the thirteen filters, the
 * four sorts, the sparse URL, the chips and the counting, and the screen that
 * mounts it is the criterion's route with three values changed.
 *
 * The spec is written out rather than a shared `EMPTY_LIST_SPEC` constant: a
 * spec is generic in two type parameters, so a shared one would have to be a
 * factory, and a factory returning six no-ops is longer than the six no-ops. The
 * day a protocol earns a filter of its own, the shape to grow is right here.
 *
 * Every function below is a binding, exactly like `criterion-filters.ts`: a
 * caller writes `filterProtocols(cards, filters)` and not
 * `filterVersioned(cards, filters, PROTOCOL_LIST_SPEC)` eight times on one
 * screen.
 */

export const PROTOCOL_LIST_SPEC: VersionedListSpec<ProtocolCard, ProtocolFilters> = {
  defaults: PROTOCOL_FILTERS_DEFAULT,

  /** No own dimension to read out of a pasted link (§15). */
  read: () => ({}),

  /** And none to write back into it. */
  write: () => ({}),

  /** Every card survives the object's own dimensions, because there are none. */
  matches: () => true,

  /** No own chip: the summary shows the common ones and nothing else. */
  chips: () => [],

  /** No own sort: the four common ones are all a protocol orders by (§15). */
  sorts: {},
};

/* ------------------------------------------------- the layer, bound to §7 */

export type ProtocolSearch = VersionedSearch<ProtocolFilters>;
export type { VersionedCounts as ProtocolCounts };

export const readProtocolFilters = (search: Record<string, unknown>): ProtocolSearch =>
  readVersionedFilters(search, PROTOCOL_LIST_SPEC);

export const protocolFiltersOf = (search: ProtocolSearch): ProtocolFilters =>
  versionedFiltersOf(search, PROTOCOL_LIST_SPEC);

export const protocolSearchOf = (filters: ProtocolFilters): ProtocolSearch =>
  versionedSearchOf(filters, PROTOCOL_LIST_SPEC);

export const filterProtocols = (
  cards: readonly ProtocolCard[],
  filters: ProtocolFilters,
): ProtocolCard[] => filterVersioned(cards, filters, PROTOCOL_LIST_SPEC);

export const protocolCounts = (
  cards: readonly ProtocolCard[],
  filters: ProtocolFilters,
): VersionedCounts => versionedCounts(cards, filters, PROTOCOL_LIST_SPEC);

/** « 6 protocoles » with nothing filtered, « 2 protocoles sur 6 » with something (§4). */
export const protocolCountLabel = (counts: VersionedCounts): string =>
  versionedCountLabel(counts, PROTOCOL_WORDING.noun);

export const protocolAuthors = versionedAuthors;

export const activeProtocolFilters = (
  filters: ProtocolFilters,
): VersionedFilterChip<ProtocolFilters>[] => activeVersionedFilters(filters, PROTOCOL_LIST_SPEC);

export const hasActiveProtocolFilters = (filters: ProtocolFilters): boolean =>
  hasActiveVersionedFilters(filters, PROTOCOL_LIST_SPEC);

export const hasHiddenProtocolFilter = (filters: ProtocolFilters): boolean =>
  hasHiddenVersionedFilter(filters, PROTOCOL_LIST_SPEC);

export const resetProtocolFilters = (filters: ProtocolFilters): ProtocolFilters =>
  resetVersionedFilters(filters, PROTOCOL_LIST_SPEC);
